import { observable, action, makeObservable } from 'mobx';
import { IPattern } from 'types';
import { toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';

import {
  createPatternService,
  deletePatternService,
  updatePatternSerive,
  getPatternService,
  IAddPatternData,
  IEditPatternData
} from 'services/pattern';

export type IInitialState = {
  data: {
    patterns: IPattern[];
    editPatternData: IPattern | null;
  };
  ui: {
    getPatternLoading: boolean;
    addPatternLoading: boolean;
    editPatternLoading: string | null;
    deletePatternLoading: string | null;
  };
};

export const initialState: IInitialState = {
  data: {
    patterns: [],
    editPatternData: null
  },
  ui: {
    getPatternLoading: false,
    addPatternLoading: false,
    editPatternLoading: null,
    deletePatternLoading: null
  }
};

export class PatternStore {
  state: IInitialState = initialState;

  getPattern = async () => {
    try {
      this.setState({ ui: { ...this.state.ui, getPatternLoading: true } });
      const { data } = await getPatternService();
      this.setState({ data: { ...this.state.data, patterns: data || [] } });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getPatternLoading: false } });
    }
  };

  createPattern = async (params: IAddPatternData) => {
    try {
      this.setState({ ui: { ...this.state.ui, addPatternLoading: true } });
      await createPatternService(params);
      await this.getPattern();
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addPatternLoading: false } });
    }
  };

  deletePattern = async (params: string) => {
    try {
      this.setState({ ui: { ...this.state.ui, deletePatternLoading: params } });
      await deletePatternService(params);
      await this.getPattern();
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deletePatternLoading: null } });
    }
  };

  updatePattern = async (params: IEditPatternData) => {
    try {
      this.setState({
        ui: { ...this.state.ui, editPatternLoading: params.id }
      });
      await updatePatternSerive(params);
      await this.getPattern();
      this.setState({ data: { ...this.state.data, editPatternData: null } });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, editPatternLoading: null } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };

  constructor() {
    makeObservable(this, {
      state: observable,
      getPattern: action,
      createPattern: action,
      deletePattern: action,
      updatePattern: action,
      setState: action,
      reset: action
    });
  }
}
